/**
 * Continuous-merge utilities (no merge events).
 *
 *   * computeSminK(a, b) — pairwise smin smoothness for the renderer's chain
 *     smin. Distance-based: large k when stacked, vanishingly small when
 *     well-separated. The visual unification of touching blobs is purely
 *     a shader effect.
 *
 *   * applySurfaceTension(blobs, shortEdge, dt) — Lennard-Jones-like
 *     conservative spring + axial-relative-velocity damping in a
 *     hybrid integration scheme:
 *       - Spring is applied as a force via `applyForce`, integrated by the
 *         outer Verlet step. (Within the spring's stable dt regime.)
 *       - Damping is applied SEMI-IMPLICITLY: the axial component of each
 *         body's COM-frame velocity is multiplied by `1/(1+c·dt/m)` and
 *         encoded back via `setVelocity`. This form is unconditionally
 *         stable for any coefficient and time step — fixes the explicit-
 *         damping divergence (`c·dt/m = 72` ≫ stability bound 2) that
 *         catapulted contacting pairs apart at ~70× their closing speed.
 *
 *     Centre-of-mass momentum is preserved (axial-only damping in the COM
 *     frame). Perpendicular motion (sliding past each other) is untouched.
 *
 * The discrete merge transition (mergeT, beginMerge/executeMerge) and the
 * deep-overlap absorption (absorbDeepOverlap, fuseInto) are gone. Blobs
 * remain independent particles for the lifetime of the run.
 */
import type { BlobBody } from './blob.js';

const SMIN_K_FAR = 0.005;
const SMIN_K_NEAR = 0.5;

function smoothstep01(x: number): number {
  const t = x < 0 ? 0 : x > 1 ? 1 : x;
  return t * t * (3 - 2 * t);
}

/**
 * Pairwise smin k (in pixels). Returns the smoothness the renderer should
 * use when chain-smin'ing this pair into the combined SDF.
 *
 *   norm = clamp(centerDist / (rA + rB), 0, 1)
 *   t    = smoothstep(0, 1, norm)
 *   k    = mix(SMIN_K_NEAR, SMIN_K_FAR, t) * radAvg
 */
export function computeSminK(
  a: { position: [number, number]; radius: number },
  b: { position: [number, number]; radius: number },
): number {
  const radSum = a.radius + b.radius;
  if (radSum <= 1e-5) return 0;
  const dx = b.position[0] - a.position[0];
  const dy = b.position[1] - a.position[1];
  const dist = Math.hypot(dx, dy);
  const norm = Math.min(1, dist / radSum);
  const t = smoothstep01(norm);
  const radAvg = 0.5 * radSum;
  return (SMIN_K_NEAR * (1 - t) + SMIN_K_FAR * t) * radAvg;
}

/** Lennard-Jones-like spring stiffness (force per unit normalised displacement). */
const SURFACE_TENSION_STIFFNESS = 12.0;
/** Damping coefficient on the axial relative velocity. */
const SURFACE_TENSION_DAMPING = 4.0;
/** Equilibrium centre-distance as a fraction of radSum (deep overlap).
 *  At 0.92 the pair settled with only ~8% overlap — the smin bridge was
 *  thin and the centres read as two distinct blobs. At 0.5 each centre
 *  sits at the other's surface, the body reads as a single fluid mass,
 *  and the smin's per-pair k (which peaks near norm = 0.5 in
 *  computeSminK's smoothstep) renders the bridge wide and smooth. */
const EQUILIBRIUM_OFFSET = 0.5;
/** Engaged when |normalised displacement| < ACTIVE_RANGE. */
const ACTIVE_RANGE = 0.30;

/**
 * Lennard-Jones-style surface tension between every pair of blobs.
 * See module header for the rationale.
 *
 *   r_eq         = radSum * EQUILIBRIUM_OFFSET
 *   displacement = (centerDist - r_eq) / r_eq          // dimensionless
 *   |displacement| > ACTIVE_RANGE → skip (out of contact band)
 *
 * Spawn ramp:
 *   Both spring and damping are scaled by `gate = a.spawnInfluence *
 *   b.spawnInfluence` (= spawnT_a^3 · spawnT_b^3), mirroring the long-
 *   range attraction in world.ts. This eliminates the step in the force
 *   field that previously fired at spawnT == 1 and injected an impulse
 *   whenever a fresh blob's spawn animation completed inside the contact
 *   band of an existing blob. Gating both spring and damping with the
 *   same factor keeps the stiffness/damping ratio (= the system Q)
 *   invariant during the ramp.
 *
 * Spring (explicit, via applyForce):
 *   F_spring = -stiffness × gate × displacement
 *               (positive when too close → push apart;
 *                negative when too far  → pull together)
 *   a feels  -F_spring·u, b feels  +F_spring·u  (Newton's 3rd law,
 *   u points from a to b).
 *
 * Damping (semi-implicit, via setVelocity):
 *   Decompose each body's velocity into a centre-of-mass component (shared)
 *   and a COM-frame component (equal magnitude, opposite signs). Project
 *   the COM-frame component onto the contact axis and shrink that axial
 *   component by  dampFactor = 1 / (1 + c·gate·dt/m). Reconstruct the
 *   velocity and write it back via setVelocity. This implicit-Euler form
 *   keeps |dampFactor| < 1 unconditionally — no matter how large c·dt is
 *   — so the integration cannot diverge from large damping.
 *
 *   Centre-of-mass momentum is preserved (axial reduction is symmetric in
 *   COM frame). Perpendicular motion is untouched (sliding past each other
 *   is unaffected).
 *
 * Stiffness and damping both scale linearly with shortEdge so the dynamics
 * stay visually comparable across resolutions.
 *
 * NOTE on mass: dampFactor uses an effective mass = 1.0 (the canonical
 * blob mass at the moment). For blobs of mass m ≠ 1 the strict reduced-
 * mass form would be `1/(1 + 2·c·dt/m)`, but all current spawn code uses
 * mass = 1 and we keep the constant-form for clarity.
 */
export function applySurfaceTension(
  blobs: BlobBody[],
  shortEdge: number,
  dt: number,
): void {
  if (blobs.length < 2 || dt <= 0) return;

  const stiffness = SURFACE_TENSION_STIFFNESS * shortEdge;
  const damping = SURFACE_TENSION_DAMPING * shortEdge;

  for (let i = 0; i < blobs.length; i++) {
    const a = blobs[i];
    if (!a) continue;
    for (let j = i + 1; j < blobs.length; j++) {
      const b = blobs[j];
      if (!b) continue;

      // Cubic two-side spawn gate, mirroring the attraction force in
      // world.ts. Eliminates the step at spawnT == 1 that previously
      // injected an impulse whenever a fresh blob's spawn animation
      // completed inside an existing blob's contact band.
      const gate = a.spawnInfluence * b.spawnInfluence;
      if (gate <= 0) continue;

      const dx = b.position[0] - a.position[0];
      const dy = b.position[1] - a.position[1];
      const centerDist = Math.hypot(dx, dy);
      if (centerDist < 1e-6) continue;

      const radSum = a.radius + b.radius;
      if (radSum <= 1e-5) continue;
      const r_eq = radSum * EQUILIBRIUM_OFFSET;
      const displacement = (centerDist - r_eq) / r_eq;
      if (Math.abs(displacement) > ACTIVE_RANGE) continue;

      const ux = dx / centerDist;
      const uy = dy / centerDist;

      // ---- Spring (explicit, conservative) ---------------------------------
      // Positive when too close (push apart); negative when too far (pull
      // together). The applyForce sign flip below routes it correctly.
      const springForce = -stiffness * gate * displacement;
      a.applyForce(-springForce * ux, -springForce * uy);
      b.applyForce(springForce * ux, springForce * uy);

      // ---- Damping (semi-implicit) -----------------------------------------
      // Work in the centre-of-mass frame. The axial part of each body's
      // COM-frame velocity is shrunk by dampFactor; perpendicular motion
      // and the COM velocity itself are preserved. Per-pair gating keeps
      // dampFactor ∈ (0, 1] for any c·gate·dt ≥ 0 (unconditionally stable).
      const dampFactor = 1 / (1 + damping * gate * dt);
      const va = a.getVelocity();
      const vb = b.getVelocity();

      const vComX = (va[0] + vb[0]) * 0.5;
      const vComY = (va[1] + vb[1]) * 0.5;

      const relAX = va[0] - vComX;
      const relAY = va[1] - vComY;
      const relBX = vb[0] - vComX;
      const relBY = vb[1] - vComY;

      const relAAxial = relAX * ux + relAY * uy;
      const relAPerpX = relAX - relAAxial * ux;
      const relAPerpY = relAY - relAAxial * uy;
      const relAAxialNew = relAAxial * dampFactor;

      const relBAxial = relBX * ux + relBY * uy;
      const relBPerpX = relBX - relBAxial * ux;
      const relBPerpY = relBY - relBAxial * uy;
      const relBAxialNew = relBAxial * dampFactor;

      a.setVelocity([
        vComX + relAPerpX + relAAxialNew * ux,
        vComY + relAPerpY + relAAxialNew * uy,
      ]);
      b.setVelocity([
        vComX + relBPerpX + relBAxialNew * ux,
        vComY + relBPerpY + relBAxialNew * uy,
      ]);
    }
  }
}
